import {
  getUnassignedTenderFolderURLs,
  newUnassignedTenderFolderDownload,
  loadUnassignedTenderRootFolders,
  loadUnassignedTenderSubfolders,
} from '~src/services/api';
import { DownloadFolderParams } from '~src/utils/downloader/types/types';
import { buildFolderWithPathStructure } from '~src/utils/downloader/shared';
import streamSaver from 'streamsaver';
import * as zip from '@zip.js/zip.js';
import { DownloadingState, useStore } from '~src/store';
import { Modal } from 'antd';
import type { DbTenderFolder } from '~src/types/db';
import { appLog } from '~src/utils/log';

// ==========================================
// ZIP STREAM CLASS
// ==========================================
class ZipStream {
  private zipWriter: zip.ZipWriter<WritableStream<any>> | null = null;
  private writableStream: WritableStream<any> | null = null;
  private successLog: string[] = [];
  public errorLog: string[] = [];
  private currentFileAlreadyStreamed = 0;
  private totalFiles = 0;
  private totalSize = 0;
  private startTime = 0;
  public endTime = 0;
  public fileMap: Map<string, number> = new Map();
  public isAborted = false;

  constructor(totalFiles: number, totalSize: number) {
    this.totalFiles = totalFiles;
    this.totalSize = totalSize;
    useStore.getState().changeTotalFileSize(totalSize);
  }

  start() {
    try {
      this.startTime = performance.now();
      this.writableStream = streamSaver.createWriteStream(
        `C-WEB-Project-${Date.now()}.zip`
      );
      this.zipWriter = new zip.ZipWriter(this.writableStream);
    } catch (error) {
      this.isAborted = true;
    }
  }

  getUniqueFileName(filePath: string): string {
    let uniquePath = filePath;
    let count = 1;

    const match = filePath.match(/^(.*?)(\(\d+\))?(\.[^/.]+)?$/);
    const baseName = match?.[1] || filePath;
    const extension = match?.[3] || '';

    while (this.fileMap.has(uniquePath)) {
      uniquePath = `${baseName}(${count})${extension}`;
      count++;
    }

    this.fileMap.set(uniquePath, count);
    return uniquePath;
  }

  async addFile(fullPath: string, blob: Blob) {
    if (this.isAborted || !this.zipWriter) return;

    const uniquePath = this.getUniqueFileName(fullPath);
    try {
      useStore.getState().changeDownloadingState(DownloadingState.FETCHING);
      await this.zipWriter.add(uniquePath, new zip.BlobReader(blob));
      this.currentFileAlreadyStreamed++;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);
      this.successLog.push(
        `${uniquePath} - success (${(blob.size / 1024 / 1024).toFixed(2)} MB)`
      );
    } catch (error: any) {
      this.errorLog.push(
        `${uniquePath} (0.00 MB) - ERROR: ${error?.message} | D: Folder`
      );
      useStore
        .getState()
        .addError(
          `${uniquePath} (0.00 MB) - ERROR: ${error?.message} | D: Folder`
        );
      this.isAborted = true;
    }
  }

  async addEmptyFolder(folderPath: string) {
    if (this.isAborted || !this.zipWriter) return;

    try {
      const uniqueFolderPath = folderPath.endsWith('/')
        ? folderPath
        : folderPath + '/';

      if (this.fileMap.has(uniqueFolderPath)) return;

      await this.zipWriter.add(uniqueFolderPath, new zip.TextReader(''));
      this.fileMap.set(uniqueFolderPath, 1);
    } catch (error) {
      this.errorLog.push(
        `${folderPath} - ERROR: Failed to create empty folder`
      );
    }
  }

  async finish() {
    try {
      if (!this.zipWriter || this.isAborted) return;
      if (this.endTime !== 0) return;

      this.endTime = performance.now();
      const totalSizeMB = (this.totalSize / 1024 / 1024).toFixed(2);
      const timeTaken = (this.endTime - this.startTime) / 1000;
      const speed = (this.totalSize / 1024 / 1024 / timeTaken).toFixed(2);
      const totalErrors = this.errorLog.length;

      const mergedLogs: string[] = [];
      const successEntries = [...this.successLog];
      const errorEntries = [...this.errorLog];

      while (successEntries.length || errorEntries.length) {
        if (successEntries.length) mergedLogs.push(successEntries.shift()!);
        if (errorEntries.length) mergedLogs.push(errorEntries.shift()!);
      }

      const finalLog = [
        ...mergedLogs,
        `\n==== Summary V2 ====`,
        `Total files - ${this.totalFiles}`,
        `Total errors - ${totalErrors}`,
        `Total size - ${totalSizeMB} MB`,
        `Download speed - ${speed} MB/s`,
        `Time taken - ${timeTaken.toFixed(2)} s`,
      ];

      if (totalErrors > 0) {
        finalLog.push(`\n==== ${totalErrors} Errors ====`, ...this.errorLog);
      }

      const logBlob = new Blob([finalLog.join('\n')], { type: 'text/plain' });

      if (!this.fileMap.has('download_log.txt')) {
        await this.zipWriter.add(
          'download_log.txt',
          new zip.BlobReader(logBlob)
        );
        this.fileMap.set('download_log.txt', 1);
      }

      await this.zipWriter.close();
      const clearPanel = () => {
        useStore.getState().changeDownloadingActive(false);
        useStore.getState().clearErrors();
      };
      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    } catch (error) {
      this.isAborted = true;
    }
  }
}

// ==========================================
// WORKERS CONFIGURATION
// ==========================================
const createWorker = () => {
  return new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `self.onmessage = async (event) => {
            const { file } = event.data;
            try {
              const response = await fetch(file.url);
              if (!response.ok) {
                throw new Error(\`HTTP \${response.status} - \${response.statusText}\`);
              }
              const blob = await response.blob();
              self.postMessage({ success: true, file, blob });
            } catch (error) {
              console.error(\`Worker Error - File: \${file.file_name}, Error: \${error.message}\`);
              self.postMessage({ success: false, file, error: error.message });
            }
          };`,
        ],
        { type: 'application/javascript' }
      )
    )
  );
};

// ==========================================
// HELPERS
// ==========================================
const getTenderParamsFromUrl = (loc = window.location) => {
  try {
    const { pathname } = loc;
    const parts = pathname.split('/').filter(Boolean);
    const idxOf = (label: string) => parts.findIndex((p) => p === label);
    const pickAfter = (label: string) => {
      const i = idxOf(label);
      return i >= 0 && parts[i + 1] ? parts[i + 1] : undefined;
    };
    const rootFolderId = pickAfter('tender-folders');
    return { rootFolderId };
  } catch {
    return {};
  }
};

const uniqFoldersById = (folders: DbTenderFolder[] | any[]) => {
  const map = new Map<string, any>();
  for (const f of folders) {
    if (f?.id) map.set(String(f.id), f);
  }
  return Array.from(map.values());
};

const ModalConfirmDownload = (): Promise<boolean> => {
  function onCancelfunction() {
    useStore.getState().changeDownloadingState('FINISHING');
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 1500);
  }
  return new Promise((resolve) => {
    Modal.confirm({
      title: 'Warning',
      content:
        'Selected download folder(s) is too big (500+ files). There can be problems with downloading. We recommend downloading manually if something goes wrong.',
      onOk: () => resolve(true),
      onCancel: () => {
        onCancelfunction();
        resolve(false);
      },
      okText: 'I understand',
      cancelText: 'Cancel',
    });
  });
};

const loadAllUnassignedTenderSubfoldersRecursively = async (
  projectId: string | number,
  startFolderIds: number[]
): Promise<DbTenderFolder[]> => {
  const visited = new Set<number>();
  const result: DbTenderFolder[] = [];
  const queue: number[] = [...startFolderIds];

  while (queue.length) {
    const currentId = queue.shift()!;
    if (visited.has(currentId)) continue;
    visited.add(currentId);

    let children: DbTenderFolder[] = [];
    try {
      children = await loadUnassignedTenderSubfolders(projectId, currentId);
    } catch (e) {
      children = [];
    }

    if (children?.length) {
      result.push(...children);
      for (const ch of children) {
        if (ch?.id != null) queue.push(Number(ch.id));
      }
    }
  }
  return uniqFoldersById(result);
};

// ==========================================
// MAIN DOWNLOAD FUNCTION
// ==========================================
export const downloadUnassignedTenderFolder = async ({
  folder,
  allFolders,
  fileVersionsContext,
  onDownloadStarted,
  onLargeFiles,
}: DownloadFolderParams): Promise<void> => {
  try {
    const { rootFolderId } = getTenderParamsFromUrl();
    const projectId = folder.project_id;
    const rootFolderName = folder.name;

    // -------------------------------------------------------------
    // PREPARE: Global Context Tree
    // -------------------------------------------------------------
    let rootFolders: any[] = [];
    try {
      rootFolders = await loadUnassignedTenderRootFolders(projectId);
    } catch (e) {}

    const allDescendants = await loadAllUnassignedTenderSubfoldersRecursively(
      projectId,
      rootFolders.map((f) => Number(f.id))
    );

    const fullContextTree = uniqFoldersById([
      ...(allFolders || []),
      ...(rootFolders || []),
      ...(allDescendants || []),
      folder,
    ]);

    // -------------------------------------------------------------
    // BUILD PATHS
    // -------------------------------------------------------------
    const structure = buildFolderWithPathStructure(
      folder as any,
      fullContextTree as any
    );

    const rootFolderEntry = structure.find(
      (s: any) => String(s.id) === String(folder.id)
    );

    let parentPathPrefix = '';
    if (rootFolderEntry && rootFolderEntry.path) {
      const parts = rootFolderEntry.path.split('/');
      if (parts.length > 1) {
        parts.pop();
        parentPathPrefix = parts.join('/') + '/';
      }
    }

    // Prepare empty folders list
    const folderPathsToCreate = uniqFoldersById(
      structure.map((s: any) => ({ id: s.id, name: s.path } as any))
    )
      .map((x: any) => x.name)
      .filter(Boolean)
      .map((p: string) => {
        if (parentPathPrefix && p.startsWith(parentPathPrefix)) {
          return p.substring(parentPathPrefix.length);
        }
        return p;
      });

    const allPathsSet = new Set<string>(folderPathsToCreate);
    if (!allPathsSet.has(rootFolderName)) {
      folderPathsToCreate.unshift(rootFolderName);
    }

    // -------------------------------------------------------------
    // FETCH FILES
    // -------------------------------------------------------------
    const files = await newUnassignedTenderFolderDownload('files', {
      projectId: folder.project_id,
      rootFolderId: rootFolderId || folder.parent_id || folder.id,
      folderId: folder.id,
    });

    let startDownload = true;
    if (files.length > 500) {
      startDownload = await ModalConfirmDownload();
    }

    if (!startDownload) return;

    if (onDownloadStarted) {
      onDownloadStarted();
    }

    const totalSize = files.reduce(
      (acc, file) => acc + (file.file_size || 0),
      0
    );

    const zipStream = new ZipStream(files.length, totalSize);
    useStore.getState().totalDownloadFiles(files.length);
    zipStream.start();

    if (zipStream.isAborted) return;

    // Create empty folders
    const uniquePaths = [...new Set(folderPathsToCreate)];
    for (const path of uniquePaths) {
      await zipStream.addEmptyFolder(path);
    }

    if (!files.length) {
      await zipStream.finish();
      return;
    }

    // -------------------------------------------------------------
    // WORKER QUEUE
    // -------------------------------------------------------------
    const workers: Worker[] = [];
    const fileQueue = [...files];
    let activeWorkers = 0;
    const NUM_WORKERS = 5;

    for (let i = 0; i < NUM_WORKERS; i++) {
      workers.push(createWorker());
    }

    const processFile = async (file) => {
      const [urlData] = await getUnassignedTenderFolderURLs(
        Number(folder.project_id),
        [file.using_key]
      );

      const fileURL = urlData?.url;
      if (!fileURL) {
        zipStream.errorLog.push(`${file.file_name} - ERROR: Missing URL`);
        useStore.getState().addError(`${file.file_name} - ERROR: Missing URL`);
        return null;
      }

      return { ...file, url: fileURL };
    };

    const assignFileToWorker = async (worker: Worker) => {
      if (zipStream.isAborted) return;

      const nextFile = fileQueue.shift();
      if (!nextFile) {
        if (activeWorkers === 0) {
          setTimeout(async () => {
            if (activeWorkers === 0) {
              await zipStream.finish();
              workers.forEach((worker) => worker.terminate());
            }
          }, 500);
        }
        return;
      }

      const processedFile = await processFile(nextFile);
      if (!processedFile) {
        await assignFileToWorker(worker);
        return;
      }

      activeWorkers++;
      worker.postMessage({ file: processedFile });

      worker.onmessage = async (event) => {
        try {
          const { success, file, blob, error } = event.data;
          if (!file) return;

          // Use the exact stripped path logic
          const rawFolderPath =
            structure.find((f: any) => String(f.id) === String(file.folder_id))
              ?.path || '';

          let safeFolderPath = rawFolderPath;
          if (parentPathPrefix && rawFolderPath.startsWith(parentPathPrefix)) {
            safeFolderPath = rawFolderPath.substring(parentPathPrefix.length);
          }
          if (!safeFolderPath) safeFolderPath = rootFolderName;

          const fullPath = safeFolderPath
            ? `${safeFolderPath}/${file.file_name}`
            : file.file_name;

          if (success) {
            await zipStream.addFile(fullPath, blob);
          } else {
            zipStream.errorLog.push(`${fullPath} - ERROR: ${error}`);
            useStore.getState().addError(`${fullPath} - ERROR: ${error}`);
          }

          activeWorkers--;

          if (fileQueue.length > 0) {
            await assignFileToWorker(worker);
          } else if (activeWorkers === 0) {
            setTimeout(async () => {
              if (activeWorkers === 0) {
                await zipStream.finish();
                workers.forEach((worker) => worker.terminate());
              }
            }, 500);
          }
        } catch (err: any) {
          zipStream.errorLog.push(`Worker processing error - ${err.message}`);
          useStore
            .getState()
            .addError(`Worker processing error - ${err.message}`);
        }
      };
    };

    for (const worker of workers) {
      await assignFileToWorker(worker);
    }
  } catch (error) {
    appLog('downloadUnassignedTenderFolder -> global error', error);
    useStore.getState().changeDownloadingActive(false);
  }
};
